import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import api from '../../utils/axiosConfig';
import { getImageUrl } from '../../utils/imageUtils';

interface BlogPost {
    _id: string;
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    featuredImage?: string;
    category: string;
    tags: string[];
    author: {
        name: string;
        email?: string;
        avatar?: string;
    };
    readTime: number;
    publishedAt?: string;
    views: number;
}

const BlogDetails: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<BlogPost | null>(null);
    const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (slug) {
            fetchBlogPost();
        }
    }, [slug]);

    const fetchBlogPost = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/blogs/${slug}`);
            setPost(response.data.data);
            
            // Fetch related posts
            if (response.data.data) {
                const relatedResponse = await api.get('/blogs', {
                    params: {
                        category: response.data.data.category,
                        limit: 4
                    }
                });
                const related = (relatedResponse.data.data || []).filter((p: BlogPost) => p._id !== response.data.data._id).slice(0, 3);
                setRelatedPosts(related);
            }
        } catch (error: any) {
            console.error('Failed to fetch blog post:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white">
                <PublicHeader />
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-32">
                    <div className="text-center">
                        <div className="spinner mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading blog post...</p>
                    </div>
                </div>
                <PublicFooter />
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen bg-white">
                <PublicHeader />
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-32">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-[#3B3B3B] mb-4">Post Not Found</h1>
                        <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
                        <Link
                            to="/blog"
                            className="inline-flex items-center px-6 py-3 bg-[#3B82F6] text-white rounded-xl hover:bg-blue-700 transition-all font-semibold"
                        >
                            Back to Blog
                        </Link>
                    </div>
                </div>
                <PublicFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <PublicHeader />

            {/* Hero Section */}
            <section className="pt-32 pb-12 bg-gradient-to-br from-[#F8FAFC] via-white to-blue-50/30 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#3B82F6]/10 rounded-full blur-3xl"></div>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto">
                        <Link
                            to="/blog"
                            className="inline-flex items-center text-[#3B82F6] hover:text-blue-700 mb-6 font-medium transition-colors"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Blog
                        </Link>

                        <div className="mb-6">
                            <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#3B82F6] to-blue-600 text-white rounded-full text-sm font-semibold shadow-lg">
                                {post.category}
                            </span>
                        </div>

                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#3B3B3B] mb-6 leading-tight">
                            {post.title}
                        </h1>

                        <div className="flex flex-wrap items-center gap-4 text-gray-600">
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-[#3B82F6] to-blue-600 rounded-full flex items-center justify-center mr-3">
                                    <span className="text-white font-bold">{post.author.name[0]}</span>
                                </div>
                                <span className="font-medium text-[#3B3B3B]">{post.author.name}</span>
                            </div>
                            <span className="text-gray-400">•</span>
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not published'}</span>
                            </div>
                            <span className="text-gray-400">•</span>
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{post.readTime} min read</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Image */}
            {post.featuredImage && (
                <section className="py-8 bg-white">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto">
                            <img
                                src={getImageUrl(post.featuredImage)}
                                alt={post.title}
                                className="w-full h-auto rounded-2xl shadow-lg"
                            />
                        </div>
                    </div>
                </section>
            )}

            {/* Article Content */}
            <article className="py-12 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <div className="prose prose-lg max-w-none">
                            <div 
                                className="text-gray-700 leading-relaxed blog-content"
                                dangerouslySetInnerHTML={{ __html: post.content }}
                            />
                        </div>

                        {/* Tags */}
                        <div className="mt-12 pt-8 border-t border-gray-200">
                            <h3 className="text-lg font-bold text-[#3B3B3B] mb-4">Tags</h3>
                            <div className="flex flex-wrap gap-2">
                                {post.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-[#3B82F6] hover:text-white transition-colors cursor-pointer"
                                    >
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </article>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-6xl mx-auto">
                            <h2 className="text-2xl font-bold text-[#3B3B3B] mb-12 text-center">Related Articles</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {relatedPosts.map((relatedPost) => (
                                    <Link
                                        key={relatedPost._id}
                                        to={`/blog/${relatedPost.slug}`}
                                        className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-[#3B82F6] hover:-translate-y-2"
                                    >
                                        <div className="relative h-40 overflow-hidden">
                                            {relatedPost.featuredImage ? (
                                                <img
                                                    src={getImageUrl(relatedPost.featuredImage)}
                                                    alt={relatedPost.title}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-[#3B82F6] to-blue-600">
                                            <div className="absolute inset-0 bg-gradient-to-br from-[#3B82F6]/50 to-[#F59E0B]/50 group-hover:scale-110 transition-transform duration-300"></div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-6">
                                            <span className="inline-block px-3 py-1 bg-blue-50 text-[#3B82F6] rounded-full text-xs font-semibold mb-3">
                                                {relatedPost.category}
                                            </span>
                                            <h3 className="text-lg font-bold text-[#3B3B3B] mb-2 group-hover:text-[#3B82F6] transition-colors line-clamp-2">
                                                {relatedPost.title}
                                            </h3>
                                            <p className="text-gray-600 text-sm line-clamp-2">
                                                {relatedPost.excerpt}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <PublicFooter />
        </div>
    );
};

export default BlogDetails;
